<script setup lang="ts">
import { useQuizterStore } from '../stores/quizterStore';
import { storeToRefs } from 'pinia';
import { onMounted, watch } from 'vue';
import NextButton from '../components/NextButton.vue';
import AddVideoDialog from '../components/host/AddVideoDialog.vue';
import EditVideoQuizDialog from '../components/host/EditVideoQuizDialog.vue';
import VideoList from '../components/VideoList.vue';
import QuizterLayout from '../layout/QuizterLayout.vue';
import useLocale from '../../composables/use-locale';
import { useVideoQuestions } from '../composables/useVideoQuestions';
import { useVideoSelection } from '../composables/useVideoSelection';

const props = defineProps<{ resourceId: string; sourceName?: string; sourceThumbnail?: string }>();

const emit = defineEmits<{
  start: [resourceIds: string[]];
  back: [];
}>();

const { t } = useLocale();

const quizterStore = useQuizterStore();
const { isLoading, isConnected } = storeToRefs(quizterStore);

const {
  videos,
  excludedResourceIds,
  showAddVideoDialog,
  showEditDialog,
  editVideo,
  openAddVideoDialog,
  handleAddVideo,
  handleRemoveVideo,
  handleEditVideoQuiz,
  handleEditSave,
} = useVideoSelection({ allowRemoveAll: false });

// Keeps the pinned first video in sync with props, including when they resolve asynchronously.
watch(
  () => ({ resourceId: props.resourceId, title: props.sourceName || '', thumbnail: props.sourceThumbnail }),
  (meta) => {
    videos.value[0] = { ...videos.value[0], ...meta };
  },
  { immediate: true },
);

onMounted(async () => {
  if (videos.value[0]?.selectedQuestions?.length) {
    return;
  }
  const { questions, fetch } = useVideoQuestions(props.resourceId);
  await fetch();
  if (questions.value.length > 0) {
    videos.value[0] = { ...videos.value[0], selectedQuestions: questions.value };
  }
});

const handleStartGame = () => {
  const resourceIds = videos.value.map((v) => v.resourceId);
  emit('start', resourceIds);
};
</script>

<template>
  <QuizterLayout :show-header="false" :show-back-button="true" back-button-pos="topLeft" @back="emit('back')">
    <div class="d-flex flex-column align-center justify-center ga-12">
      <h1 class="chalkduster-font text-white text-center text-h1">Quizter!</h1>

      <VideoList
        v-if="isConnected"
        :videos="videos"
        :allow-remove-all="false"
        :show-edit="true"
        @remove="handleRemoveVideo"
        @edit="handleEditVideoQuiz"
      >
        <v-btn
          variant="outlined"
          class="add-video-btn"
          color="white"
          rounded="lg"
          :ripple="true"
          @click="openAddVideoDialog"
        >
          <div class="d-flex flex-column align-center">
            <v-icon icon="fa:fawsb fa-video" size="60" color="white"></v-icon>
            <p class="chalkboard-font text-white text-center text-h6 ma-0">
              {{ t('quizter.add_video_clarification_home_page') }}
            </p>
          </div>
        </v-btn>
      </VideoList>
      <v-progress-circular v-else indeterminate color="white" size="64"></v-progress-circular>

      <NextButton
        v-if="isConnected"
        size="large"
        @click="handleStartGame"
        :loading="isLoading"
        data-testid="quizter-start-button"
      >
        <template #prepend>
          <v-icon class="mr-2" icon="fa:fawsb fa-trophy"></v-icon>
        </template>
        {{ t('quizter.start_game') }}
      </NextButton>

      <!-- Add Video Dialog -->
      <AddVideoDialog
        v-model="showAddVideoDialog"
        :exclude-resource-ids="excludedResourceIds"
        @select="handleAddVideo"
      />

      <!-- Edit Video quiz Dialog-->
      <EditVideoQuizDialog v-if="!!editVideo" v-model="showEditDialog" :video="editVideo" @save="handleEditSave" />
    </div>
  </QuizterLayout>
</template>

<style scoped lang="scss">
.add-video-btn {
  height: 191px;
  width: 340px;
  border: 3px solid #f2f2f3;
}
</style>
